import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(duration)
dayjs.extend(relativeTime)

export const formatPrice = function (price: number, addPlus = false, priceSymbol = 'Credit') {
  if (isNaN(Number(price))) {
    return price
  }

  if (priceSymbol !== '') {
    priceSymbol = price > 100 ? 'Credits' : 'Credit'
  }

  return formatMillicentPrice(price * 1000, addPlus, priceSymbol)
}

export const formatPriceWithoutSymbol = function (price: number) {
  if (isNaN(Number(price))) {
    return price
  }

  return formatMillicentPriceWithoutSymbol(price * 1000)
}

export function formatMillicentPrice(price: number, addPlus = false, priceSymbol = 'Credit') {
  if (isNaN(Number(price))) {
    return price
  }

  price = Math.round(price) / 100000

  let isMinus = false
  if (price < 0) {
    isMinus = true
  }

  if (Math.abs(price) > 1000000000) {
    return `${isMinus ? '-' : ''}9,999,999+`
  }

  const resultString = Math.abs(price).toFixed(3)
  const resultList = resultString.split('')
  resultList.push(' ' + priceSymbol)

  if (isMinus) {
    resultList.unshift('-')
  } else if (!isMinus && Math.abs(price) && addPlus) {
    resultList.unshift('+')
  }

  return resultList.join('')
}

export function formatMillicentPriceWithoutSymbol(price: number) {
  if (isNaN(Number(price))) {
    return price
  }

  price = Math.round(price) / 100000

  if (Math.abs(price) > 1000000000) {
    return 9999999
  }

  const resultString = Math.abs(price).toFixed(3)
  const resultList = resultString.split('')

  return Number(resultList.join(''))
}

export function formatDomain(domain: number) {
  return `#${domain + 1}`
}

export function formatRegion(domain: number) {
  return `Region ${domain + 1}`
}

const PRESET_FMT = {
  day: 'YYYY-MM-DD',
  minute: 'YYYY-MM-DD HH:mm',
  second: 'YYYY-MM-DD HH:mm:ss',
} as const
export const formatDatePreset = function (
  date: string | number | Date | dayjs.Dayjs,
  type: keyof typeof PRESET_FMT | string = 'day',
) {
  if (!date) {
    return '-'
  }

  let fmt = type
  if (Object.keys(PRESET_FMT).includes(type)) {
    fmt = PRESET_FMT[type as keyof typeof PRESET_FMT]
  }
  return dayjs(date).format(fmt)
}

export function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 B'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export function formatGB2Bytes(gb: number) {
  return gb * 1024 * 1024 * 1024
}

export function formatMB2Bytes(mb: number) {
  return mb * 1024 * 1024
}

export const formatDuration = function (timeSec: number) {
  return dayjs.duration(timeSec, 'seconds').humanize()
}

export const formatDurationToHours = function (timeSec: number) {
  return dayjs.duration(timeSec, 'seconds').asHours().toFixed(1)
}

export const formatRamMB = (ramMB: number) => {
  if (ramMB > 1024) {
    return `${parseInt(ramMB / 1024 + 0.5 + '')} GB`
  } else {
    return `${ramMB} MB`
  }
}

export function formatToDynamicDecimalPlaces(value: number) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

export function formatBandwidthBit(value: number) {
  if (value >= 1e9) {
    return (value / 1e9).toFixed(1) + ' Gbps'
  } else if (value >= 1e6) {
    return (value / 1e6).toFixed(0) + ' Mbps'
  } else if (value >= 1e3) {
    return (value / 1e3).toFixed(0) + ' Kbps'
  } else {
    return value + ' bps'
  }
}

export function maskPassword(password: string): string {
  if (password.length <= 3) {
    return password
  }
  return password.slice(0, 3) + '*'.repeat(password.length - 3)
}

export function formatEthereumAddress(
  address: string,
  prefixLen: number = 4,
  suffixLen: number = 4,
): string {
  const cleanedAddress = address.startsWith('0x') ? address.substring(2) : address

  if (cleanedAddress.length < prefixLen + suffixLen) {
    return address
  }

  const prefix = cleanedAddress.substring(0, prefixLen)
  const suffix = cleanedAddress.substring(cleanedAddress.length - suffixLen)

  return `0x${prefix}...${suffix}`
}

export function formatUserName(name: string): string {
  if (name.startsWith('0x')) {
    return formatEthereumAddress(name)
  } else {
    return name.split('@')[0]
  }
}
